import { boxCategoryFromSlug, boxCategorySlug } from "@loot-os/api-contract";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";
import { type BoxListSearch, parseBoxListSearch } from "../../shared/boxListSearch.js";

/**
 * The boxes page's filters as its URL carries them, so a filtered view survives opening a box and
 * coming back: New or every box, a category, and the list filters. A default is left out.
 */
export interface BoxesSearch extends BoxListSearch {
  tab?: "new";
  /** A box category, by its slug (`one-piece`); one the list does not have is dropped. */
  category?: string;
}

/** The route's `validateSearch`: anything it does not recognize is dropped, never an error. */
export function parseBoxesSearch(search: Record<string, unknown>): BoxesSearch {
  const category = boxCategoryFromSlug(search.category);
  return {
    ...(search.tab === "new" ? { tab: "new" as const } : {}),
    ...(category ? { category: boxCategorySlug(category) } : {}),
    ...parseBoxListSearch(search),
  };
}

/** The boxes page's filters, and a writer that replaces the URL's entry rather than adding one. */
export function useBoxesSearch(): [BoxesSearch, (patch: Partial<BoxesSearch>) => void] {
  const search = useSearch({ from: "/boxes" });
  const navigate = useNavigate({ from: "/boxes" });
  const update = useCallback(
    (patch: Partial<BoxesSearch>) => {
      void navigate({
        search: (prev) => parseBoxesSearch({ ...prev, ...patch }),
        replace: true,
      });
    },
    [navigate],
  );
  return [search, update];
}
